import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Center,
  Heading,
  Pressable,
  Spinner,
  Text,
  useDisclose,
  VStack,
} from "native-base";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import moment from "moment";
import styles from "./styles.module.css";
import colors from "../../../constants/colors";
import { StateContext, StatusContext } from "../../../contexts";
import API from "../../../api";
import { formatToCurrency, getStatus, getStatusColor } from "../../../utils";
import Cell from "../../../components/cell";
import PaymentModal from "../../../components/paymentModal";

function getType(type) {
  switch (type) {
    case "charge":
      return "COSTO TOTAL";
    case "installment":
      return "ABONO";
    default:
      return "";
  }
}

function getPaymentMethod(transaction) {
  if (transaction.amount > 0) {
    return "Cargo automático";
  } else if (transaction.payment) {
    return "Tarjeta crédito o débito";
  } else if (transaction.agencyMovement || transaction.userMovement) {
    return "Saldo Autotur";
  } else {
    return "";
  }
}

function TripsInstallmentsPage() {
  const params = useParams();
  const state = useContext(StateContext);
  const status = useContext(StatusContext);
  const [isLoading, setIsLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [date, setDate] = useState(moment());
  const {
    isOpen: isPaymentModalOpen,
    onOpen: onOpenPaymentModal,
    onClose: onClosePaymentModal,
  } = useDisclose();

  const getTransactions = useCallback(async () => {
    setIsLoading(true);
    try {
      const {
        data: { data },
        // TODO: Optimize request
      } = await API(state.jwt).get(`/tours/${params.id}`);
      const origin = data.itinerary.find(
        (location) => !location.isReturn && location.type === "origin",
      );
      setDate(moment(origin.departureDate));
      setTransactions(data.transactions);
    } catch (error) {
      status.updateErrorMessage(error);
    }
    setIsLoading(false);
  }, [params.id, state.jwt, status]);

  useEffect(() => {
    getTransactions();
  }, [getTransactions]);

  const onPay = useCallback(
    async (type, amount, file, onError) => {
      try {
        const { data } = await API(state.jwt).post(
          `/tours/${params.id}/payment`,
          {
            data: { type, amount },
          },
        );
        if (type === "card") {
          window.open(data.url, "_blank", "noopener,noreferrer");
        }
        getTransactions();
        onClosePaymentModal();
      } catch (error) {
        onError();
        status.updateErrorMessage(error);
      }
    },
    [getTransactions, onClosePaymentModal, params.id, state.jwt, status],
  );

  const pendingPayment = useMemo(
    () =>
      transactions.reduce(
        (total, transaction) =>
          total + (transaction.status === "approved" ? transaction.amount : 0),
        0,
      ),
    [transactions],
  );

  return (
    <VStack>
      <Helmet>
        <title>Gestión de cobros</title>
      </Helmet>
      <VStack backgroundColor={colors.white} paddingX={6} paddingBottom={8}>
        {isLoading ? (
          <Center height={300}>
            <Spinner size="lg" color={colors.primary} />
          </Center>
        ) : (
          <VStack space={10}>
            <VStack
              alignItems="center"
              justifyContent="center"
              backgroundColor={colors.stepper}
              borderRadius={5}
              padding={20}
              position="relative"
              minH={250}>
              <Heading color={colors.secondary} fontSize="xl">
                Pago pendiente
              </Heading>
              <Heading color={colors.secondary}>
                {pendingPayment
                  ? formatToCurrency(pendingPayment)
                  : "Liquidado"}
              </Heading>
              <Pressable
                backgroundColor={colors.primary}
                borderRadius={20}
                paddingY={2}
                paddingX={5}
                marginTop={2}
                onPress={onOpenPaymentModal}
                disabled={!pendingPayment}>
                <Text color={colors.white} textAlign="center">
                  Abonar
                </Text>
              </Pressable>
              <VStack
                backgroundColor={colors.white}
                position="absolute"
                top={6}
                right={6}
                padding={4}
                paddingRight={20}
                borderRadius={5}>
                <Heading fontSize="xl">Fecha límite</Heading>
                <Text>
                  {date
                    .clone()
                    .subtract(3, "days")
                    .format(moment.HTML5_FMT.DATE)}
                </Text>
              </VStack>
            </VStack>
            <Heading color={colors.secondary} fontSize="2xl">
              Transacciones
            </Heading>
            <table className={styles.table}>
              <thead>
                <tr>
                  <Cell color={colors.gray}>Tipo de movimiento</Cell>
                  <Cell color={colors.gray}>Fecha</Cell>
                  <Cell color={colors.gray}>Ref</Cell>
                  <Cell color={colors.gray}>Estatus</Cell>
                  <Cell color={colors.gray}>Método de pago</Cell>
                  <Cell color={colors.gray}>Importe</Cell>
                </tr>
              </thead>
              <tbody>
                {transactions
                  .sort((a, b) => b.id - a.id)
                  .map((transaction, index) => (
                    <tr key={String(index)}>
                      <Cell bold>{getType(transaction.type)}</Cell>
                      <td>
                        <VStack>
                          <Text bold>
                            {moment(transaction.createdAt).format(
                              moment.HTML5_FMT.DATE,
                            )}
                          </Text>
                          <Text>
                            {moment(transaction.createdAt).format("hh:mm a")}
                          </Text>
                        </VStack>
                      </td>
                      <Cell bold>{transaction.id}</Cell>
                      <Cell bold color={getStatusColor(transaction.status)}>
                        {getStatus(transaction.status)}
                      </Cell>
                      <Cell bold>{getPaymentMethod(transaction)}</Cell>
                      <Cell bold>{formatToCurrency(transaction.amount)}</Cell>
                    </tr>
                  ))}
              </tbody>
            </table>
          </VStack>
        )}
      </VStack>
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onRequestClose={onClosePaymentModal}
        label="Valor a abonar"
        amount={pendingPayment}
        onPay={onPay}
        editable
        maxAmount={pendingPayment}
        role="user"
      />
    </VStack>
  );
}

export default TripsInstallmentsPage;
